import React, { useEffect, useState } from "react";
import { Table, Checkbox, Select, Button, message, Card, Typography, Divider } from "antd";
import { useSelector } from "react-redux";
import axios from "axios";
import rollbackIcon from "../../assets/icons/rollback.png";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;

const roles = ["Admin", "Supplier Admin", "Supplier User"];

const disabledPermissions = {
  SUPPLIER_SEARCH: ["Supplier User", "Supplier Admin"],
  ADMIN_DASHBOARD: ["Supplier User", "Supplier Admin"],
  GOALS: ["Admin"],
  REPORTING: ["Admin"]
};

const RolesAndPermissions = () => {
  const navigate=useNavigate();
  const selectedEntities = useSelector((state) => state.entity.selectedEntities);
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await axios.get("https://techsolutionsservice.ustdev.com/entity/get", {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`
          }
        });

        const backendPermissions = res.data;

        const initialPermissions = roles.map((role) => {
          const backendEntry = backendPermissions.find((p) => p.role === role);
          const pagePermission = {};

          if (selectedEntities[0]?.length > 0) {
            selectedEntities[0].forEach((page) => {
              pagePermission[page] = {
                hasAccess: backendEntry?.pagePermission?.[page] || false,
                canDownload: backendEntry?.pagePermission?.[`${page}_DOWNLOAD`] || false
              };
            });
          }

          return { role, pagePermission };
        });

        setPermissions(initialPermissions);
      } catch (error) {
        console.error("Failed to load permissions:", error);
      }
    };

    fetchPermissions();
  }, [selectedEntities]);

  const handleCheckboxChange = (page, type, checked) => {
    setPermissions((prev) =>
      prev.map((entry) =>
        entry.role === selectedRole
          ? {
              ...entry,
              pagePermission: {
                ...entry.pagePermission,
                [page]: {
                  ...entry.pagePermission[page],
                  [type]: checked
                }
              }
            }
          : entry
      )
    );
  };

  const handleSubmit = async () => {
    const finalPayload = permissions.map((p) => {
      const flatPermissions = {};
      for (const [page, access] of Object.entries(p.pagePermission)) {
        flatPermissions[page] = access.hasAccess;
        flatPermissions[`${page}_DOWNLOAD`] = access.canDownload;
      }
      return {
        role: p.role,
        pagePermission: flatPermissions
      };
    });

    try {
      await axios.post(
        "https://techsolutionsservice.ustdev.com/entity/set",
        finalPayload,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      message.success("Permissions updated successfully");
    } catch (error) {
      console.error("Failed to send data to backend:", error);
      message.error("Failed to save permissions");
    }
  };

  const isDisabled = (page, role) => disabledPermissions[page]?.includes(role) ?? false;

  const selectedRolePermissions =
    permissions.find((p) => p.role === selectedRole)?.pagePermission || {};

  const columns = [
    {
      title: "Page",
      dataIndex: "page",
      key: "page",
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "Has Access",
      dataIndex: "hasAccess",
      key: "hasAccess",
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={selectedRolePermissions[record.page]?.hasAccess || false}
          onChange={(e) =>
            handleCheckboxChange(record.page, "hasAccess", e.target.checked)
          }
          disabled={isDisabled(record.page, selectedRole)}
        />
      )
    },
    {
      title: "Can Download",
      dataIndex: "canDownload",
      key: "canDownload",
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={selectedRolePermissions[record.page]?.canDownload || false}
          onChange={(e) =>
            handleCheckboxChange(record.page, "canDownload", e.target.checked)
          }
          disabled={isDisabled(record.page, selectedRole)}
        />
      )
    }
  ];

  const dataSource = selectedEntities[0]?.map((page, index) => ({
    key: index,
    page
  }));
  const handleBackClick = () => {
  
    navigate(`/admin/roles-permissions/entities`);
  };

  return (
    <div className="min-h-screen bg-blue-50 p-6">
        <div className="flex justify-end mb-4">
        <Button
          shape="round"
          icon={<img src={rollbackIcon || "/placeholder.svg"} alt="back" className="w-4 h-4 mr-1" />}
          size="small"
          className="bg-[#F8FAFC] h-8"
          style={{ border: "1px solid #A1BAD1" }}
          onClick={handleBackClick}
        >
          Back
        </Button>
      </div>
      <Card className="border border-gray-300 rounded-md shadow-sm">
        <Title level={2} className="text-[#2c5477] mb-6">
          Roles and Permissions Management
        </Title>

        <Divider className="my-6" />

        <div className="flex items-center mb-6">
          <label className="font-semibold mr-4 text-gray-700">Select Role:</label>
          <Select
            value={selectedRole}
            onChange={(val) => setSelectedRole(val)}
            className="w-64 rounded-md"
          >
            {roles.map((role) => (
              <Select.Option key={role} value={role}>
                {role}
              </Select.Option>
            ))}
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          className="bg-white mb-8 rounded-lg overflow-hidden"
          components={{
            header: {
              cell: (props) => (
                <th
                  {...props}
                  className="bg-[#2c5477] text-white p-4 text-center"
                />
              )
            }
          }}
        />

        <div className="flex justify-end">
          <Button
            type="primary"
            onClick={handleSubmit}
            size="large"
            className="bg-[#2c5477] border-none px-8 h-10 rounded-md text-base"
          >
            Submit Permissions
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default RolesAndPermissions;
